import dayjs, { type ConfigType } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

/** Eastern Time (EST/EDT) — default for all cuo-admin display */
export const DEFAULT_TIMEZONE = "America/New_York";

// No `z` token — it needs the advancedFormat plugin, so it printed as a literal "z".
export const DATETIME_DISPLAY_FORMAT = "MMM D, YYYY h:mm a";
export const DATETIME_DISPLAY_FORMAT_SHORT = "MMM D YYYY, h:mm a";
export const SHOW_DATETIME_DISPLAY_FORMAT = "dddd, MMM D, YYYY h:mm a [EST]";
export const DATE_DISPLAY_FORMAT = "D MMM YYYY";

/** Current moment in Eastern (use for "today", date pickers, relative ranges). */
export const dayjsTz = (...args: [] | [ConfigType?]) => {
  if (args.length === 0) return dayjs().tz(DEFAULT_TIMEZONE);
  return dayjsShow(args[0]);
};

/**
 * Show start / doors_open — naive datetimes stored as Eastern wall clock (Django USE_TZ=False).
 */
export const dayjsShow = (value: ConfigType) => {
  if (value == null || value === "") {
    return dayjs(NaN).tz(DEFAULT_TIMEZONE);
  }
  const raw = typeof value === "string" ? value.trim() : value;
  if (typeof raw === "string" && /([zZ]|[+-]\d{2}:?\d{2})$/.test(raw)) {
    return dayjs(raw).tz(DEFAULT_TIMEZONE);
  }
  return dayjs.tz(raw, DEFAULT_TIMEZONE);
};

/**
 * API/DB timestamps stored as UTC (signup_date, created_at, logs, etc.).
 */
export const dayjsUtcToTz = (value: ConfigType) => {
  if (value == null || value === "") {
    return dayjs(NaN).tz(DEFAULT_TIMEZONE);
  }
  const raw = typeof value === "string" ? value.trim() : value;
  if (typeof raw === "string" && /([zZ]|[+-]\d{2}:?\d{2})$/.test(raw)) {
    return dayjs(raw).tz(DEFAULT_TIMEZONE);
  }
  if (typeof raw === "number") {
    return dayjs(raw).tz(DEFAULT_TIMEZONE);
  }
  return dayjs.utc(raw).tz(DEFAULT_TIMEZONE);
};

export function formatUtcDateTime(
  value: ConfigType,
  format = DATETIME_DISPLAY_FORMAT,
): string {
  const d = dayjsUtcToTz(value);
  return d.isValid() ? d.format(format) : "";
}

export function formatShowDateTime(
  value: ConfigType,
  format = SHOW_DATETIME_DISPLAY_FORMAT,
): string {
  const d = dayjsShow(value);
  return d.isValid() ? d.format(format) : "";
}

export function formatUtcDate(value: ConfigType, format = DATE_DISPLAY_FORMAT): string {
  const d = dayjsUtcToTz(value);
  return d.isValid() ? d.format(format) : "";
}

export function formatShowDate(value: ConfigType, format = DATE_DISPLAY_FORMAT): string {
  const d = dayjsShow(value);
  return d.isValid() ? d.format(format) : "";
}

/** Form labels — show times are always entered and stored as Eastern wall clock. */
export const SHOW_START_DATE_LABEL = "Start Date (EST)";
export const SHOW_START_TIME_LABEL = "Start time (EST)";
export const SHOW_DOORS_OPEN_LABEL = "Doors Open (EST)";

/**
 * MUI pickers return dayjs in browser-local mode. Re-interpret the displayed
 * calendar date and clock time as Eastern wall clock (matches Django naive ET storage).
 */
export function dayjsFromEstPicker(value: ConfigType) {
  const d = dayjs(value);
  if (!d.isValid()) return dayjs(NaN).tz(DEFAULT_TIMEZONE);
  return dayjs.tz(
    `${d.format("YYYY-MM-DD")} ${d.format("HH:mm:ss")}`,
    "YYYY-MM-DD HH:mm:ss",
    DEFAULT_TIMEZONE,
  );
}

/**
 * Combine separate date + time pickers into naive Eastern "YYYY-MM-DD HH:mm"
 * for the show API (USE_TZ=False).
 */
export function combineShowDateTime(
  datePart: ConfigType,
  timePart: ConfigType,
): string {
  const date = dayjs(datePart);
  const time = dayjs(timePart);
  if (!date.isValid() || !time.isValid()) return "";
  return dayjs
    .tz(
      `${date.format("YYYY-MM-DD")} ${time.format("HH:mm")}`,
      "YYYY-MM-DD HH:mm",
      DEFAULT_TIMEZONE,
    )
    .format("YYYY-MM-DD HH:mm");
}
